import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  IconButton,
} from "@mui/material";
import { FiCheckCircle, FiX } from "react-icons/fi";
import GppBadIcon from "@mui/icons-material/GppBad";
import { v4 as uuidv4 } from "uuid";
import { API_URL } from "../constants";
import countries from "../countries.json";

const CheckoutBtns = ({
  currency,
  amount,
  onPaymentSuccess,
  buyerEmail,
  description,
  allData,
  onCancel,
  merchantId,
  accessCode,
  redirectUrl,
  cancelUrl,
  uatMode = true,
}) => {
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [paymentStatus, setPaymentStatus] = useState(null);
  const paymentWindowRef = useRef(null);
  const [orderId, setOrderId] = useState("");

  // Refs for timeouts/intervals
  const pollingIntervalRef = useRef(null);
  const paymentTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const timeLeftRef = useRef(300);

  const transactionEndpoint =
    "https://spayuattrns.bmtest.om/transaction.do?command=initiateTransaction";
  // const transactionEndpoint = uatMode
  //   ? "https://spayuattrns.bmtest.om/transaction.do?command=initiateTransaction"
  //   : "https://smartpaytrns.bankmuscat.com/transaction.do?command=initiateTransaction";

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      clearIntervalsAndTimeouts();
      closePaymentWindow();
    };
  }, []);

  const clearIntervalsAndTimeouts = () => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    if (paymentTimeoutRef.current) clearTimeout(paymentTimeoutRef.current);
  };

  const firstName = ["Full Name", "First Name", "Name"].reduce(
    (acc, key) => acc || allData?.[key],
    ""
  );
  const getCountry = countries.find((c) => c.label === allData?.Country);
  const lastName = allData?.["Last Name"] || "";
  const email = allData?.Email || buyerEmail || "";
  const country = getCountry?.code || "";
  const phone =
    `${allData?.mobile_code || allData.calling_code}${
      allData?.Mobile || allData.mobile || allData.phone || allData.Phone
    }` || "";
  const address = allData?.Address || "";
  const city = allData?.City || "";
  const postalCode = allData?.["Postal Code"] || "";
  const state = allData?.State || "";

  const generateOrderId = () => {
    // Bank Muscat requirements:
    // - Only alphanumeric characters
    // - No special characters
    // - Max 30 characters
    // - Recommended format: ORDYYYYMMDDHHMMSS + random string

    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substr(2, 6).toUpperCase();
    return `ORD${timestamp}-${random}`.replace(/[^A-Z0-9]/g, "");
  };
  const buildTransactionData = () => ({
    merchant_id: merchantId || "3971",
    order_id: generateOrderId(),
    amount: parseFloat(amount).toFixed(3),
    currency: currency,
    redirect_url:
      "https://eventegration.com/smartpay/NON_SEAMLESS_KIT/ccavResponseHandler.php",
    cancel_url:
      "https://eventegration.com/smartpay/NON_SEAMLESS_KIT/ccavResponseHandler.php",
    billing_name: `${firstName} ${lastName}` || "",
    billing_email: email || "",
    billing_address: address || "",
    billing_city: city || "",
    billing_state: state || "",
    billing_zip: postalCode || "",
    billing_country: country || "OM",
    billing_tel: phone || "",
  });

  const handlePaymentSuccess = (result) => {
    clearIntervalsAndTimeouts();
    setProcessing(false);

    switch (result.order_status) {
      case "Success":
        setPaymentStatus("success");
        onPaymentSuccess({
          transactionId: result.tracking_id,
          amount: result.amount,
          currency: result.currency,
        });
        break;

      case "Failure":
      case "Aborted":
        setPaymentStatus("failed");
        setErrorMessage(result.failure_message || "Payment failed");
        break;

      case "Timeout":
        setErrorMessage("Payment timed out - please check status later");
        break;

      default:
        setErrorMessage("Unknown payment status");
    }

    closePaymentWindow();
  };

  const startStatusPolling = (orderId) => {
    pollingIntervalRef.current = setInterval(async () => {
      try {
        // const { data } = await axios.get(
        //   `${API_URL}smartpay/status/${orderId}`
        // );

        // if (data.status !== "Awaited") {
        //   clearIntervalsAndTimeouts();
        //   handlePaymentResult(data);
        // }

        const response = await axios.get(
          `${API_URL}smartpay/status/${orderId}`
        );
        console.log(">>>>>>>>>", response);
        if (response.data.status === "Success") {
          clearInterval(pollingIntervalRef.current);
          handlePaymentSuccess(response.data);
        } else if (response.data.status === "Failure") {
          clearInterval(pollingIntervalRef.current);
          handlePaymentError(response.data.message);
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    }, 3000);
  };

  const startPaymentTimeout = () => {
    timeLeftRef.current = 300;

    paymentTimeoutRef.current = setInterval(() => {
      timeLeftRef.current -= 1;

      if (timeLeftRef.current <= 0) {
        handlePaymentError("Payment processing timed out");
      }
    }, 1000);
  };

  const openPaymentWindow = () => {
    const transactionData = buildTransactionData();
    setOrderId(transactionData.order_id);

    const form = document.createElement("form");
    form.method = "POST";
    form.action =
      "https://eventegration.com/smartpay/NON_SEAMLESS_KIT/ccavRequestHandler.php";
    form.target = "paymentWindow";

    Object.entries(transactionData).forEach(([key, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);

    const newWindow = window.open("", "paymentWindow", "width=600,height=800");
    form.submit();
    document.body.removeChild(form);

    startPaymentTimeout();

    startStatusPolling(transactionData.order_id);
  };

  const handlePaymentError = (message) => {
    clearIntervalsAndTimeouts();
    setProcessing(false);
    setPaymentStatus("failed");
    setErrorMessage(message);
    closePaymentWindow();
  };

  const closePaymentWindow = () => {
    if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
      try {
        paymentWindowRef.current.close();
      } catch (error) {
        console.log("Error closing payment window:", error);
      }
    }
    paymentWindowRef.current = null;
  };

  const renderContent = () => {
    if (errorMessage) {
      return (
        <Box sx={styles.modalContent}>
          <GppBadIcon sx={styles.errorIcon} />
          <Typography variant="h6" color="error" sx={{ mb: 1 }}>
            Payment Failed
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            {errorMessage}
          </Typography>
          <Button
            variant="contained"
            onClick={openPaymentWindow}
            disabled={processing || retryCountRef.current >= 3}
          >
            {retryCountRef.current < 3 ? "Retry Payment" : "Contact Support"}
          </Button>
        </Box>
      );
    }

    if (processing) {
      return (
        <Box sx={styles.modalContent}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Processing Payment
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Time remaining: {Math.floor(timeLeftRef.current / 60)}:
            {(timeLeftRef.current % 60).toString().padStart(2, "0")}
          </Typography>
        </Box>
      );
    }

    if (paymentStatus === "success") {
      return (
        <Box sx={styles.modalContent}>
          <FiCheckCircle style={styles.successIcon} />
          <Typography variant="h6" sx={styles.successText}>
            Payment Successful!
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={styles.modalContent}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Pay {amount} {currency}
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={openPaymentWindow}
          disabled={processing}
        >
          Proceed to Payment
        </Button>
      </Box>
    );
  };

  return (
    <Box sx={styles.dialogContent}>
      <IconButton sx={styles.closeButton} onClick={onCancel}>
        <FiX />
      </IconButton>
      {renderContent()}
    </Box>
  );
};

const styles = {
  dialogContent: {
    position: "relative",
    padding: 4,
    minWidth: 400,
    textAlign: "center",
  },
  modalContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    padding: 2,
  },
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
  },
  successIcon: {
    fontSize: 64,
    color: "#4CAF50",
  },
  errorIcon: {
    fontSize: 64,
    color: "#d32f2f",
  },
  successText: {
    color: "#4CAF50",
  },
};

export default CheckoutBtns;
